// src/store/adminStore.ts

import { create } from 'zustand';
import type { ManagedUserWithAccess, FolderTreeNode, FolderSelection } from '../models/adminTypes';
import type { AwsCredentials } from '../models/types';
import { AdminService } from '../services/adminService';
import { AccessPolicyService } from '../services/accessPolicyService';
import { S3Service } from '../services/s3Service';
import { formatAccessSummary } from '../utils/adminUtils';
import { selectionToPolicy } from '../utils/folderTreeUtils';
import { API_ENDPOINT } from '../models/constants';

export interface AdminState {
  // User list
  users: ManagedUserWithAccess[];
  usersLoading: boolean;
  usersError: string | null;

  // Folder tree
  folderTree: FolderTreeNode[];
  folderTreeLoading: boolean;

  // Permissions editor
  currentSelection: FolderSelection | null;
  savingPermissions: boolean;
  saveError: string | null;
  saveSuccess: boolean;

  // Actions
  fetchUsers: (idToken: string) => Promise<void>;
  createUser: (idToken: string, email: string) => Promise<string>;
  deleteUser: (idToken: string, userId: string) => Promise<void>;
  disableUser: (idToken: string, userId: string) => Promise<void>;
  enableUser: (idToken: string, userId: string) => Promise<void>;
  resetPassword: (idToken: string, userId: string) => Promise<void>;
  fetchFolderTree: (awsCredentials: AwsCredentials) => Promise<void>;
  setSelection: (selection: FolderSelection) => void;
  savePermissions: (idToken: string, userId: string, selection: FolderSelection) => Promise<void>;
  clearSaveStatus: () => void;
}

const adminService = new AdminService();
const accessPolicyService = new AccessPolicyService();

export const useAdminStore = create<AdminState>((set, get) => ({
  // Initial state
  users: [],
  usersLoading: false,
  usersError: null,
  folderTree: [],
  folderTreeLoading: false,
  currentSelection: null,
  savingPermissions: false,
  saveError: null,
  saveSuccess: false,

  fetchUsers: async (idToken: string) => {
    set({ usersLoading: true, usersError: null });
    try {
      const users = await adminService.listUsers(idToken);

      // Fetch access summaries for each user
      const usersWithAccess: ManagedUserWithAccess[] = await Promise.all(
        users.map(async (user) => {
          try {
            const policy = await accessPolicyService.fetchPolicy(idToken, user.userId);
            return { ...user, accessSummary: formatAccessSummary(policy) };
          } catch {
            return { ...user, accessSummary: 'No access' };
          }
        })
      );

      set({ users: usersWithAccess, usersLoading: false });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message :
        (error as { message?: string })?.message || 'Failed to load users';
      set({ usersError: message, usersLoading: false });
    }
  },

  createUser: async (idToken: string, email: string) => {
    const result = await adminService.createUser(idToken, email);
    // Refresh user list after creation
    await get().fetchUsers(idToken);
    return result.userId;
  },

  deleteUser: async (idToken: string, userId: string) => {
    await adminService.deleteUser(idToken, userId);
    // Remove from local state
    set((state) => ({
      users: state.users.filter((u) => u.userId !== userId),
    }));
  },

  disableUser: async (idToken: string, userId: string) => {
    await adminService.disableUser(idToken, userId);
    // Update local state
    set((state) => ({
      users: state.users.map((u) =>
        u.userId === userId ? { ...u, status: 'disabled' as const } : u
      ),
    }));
  },

  enableUser: async (idToken: string, userId: string) => {
    await adminService.enableUser(idToken, userId);
    // Update local state
    set((state) => ({
      users: state.users.map((u) =>
        u.userId === userId ? { ...u, status: 'active' as const } : u
      ),
    }));
  },

  resetPassword: async (idToken: string, userId: string) => {
    await adminService.resetPassword(idToken, userId);
  },

  fetchFolderTree: async (awsCredentials: AwsCredentials) => {
    set({ folderTreeLoading: true });
    try {
      const s3 = new S3Service(awsCredentials);
      const years = await s3.listYearPrefixes();

      const tree: FolderTreeNode[] = await Promise.all(
        years.map(async (year) => {
          const events = await s3.listEventPrefixes(year);
          return { year, events };
        })
      );

      // Sort years descending
      tree.sort((a, b) => b.year.localeCompare(a.year));

      set({ folderTree: tree, folderTreeLoading: false });
    } catch {
      set({ folderTree: [], folderTreeLoading: false });
    }
  },

  setSelection: (selection: FolderSelection) => {
    set({ currentSelection: selection, saveSuccess: false, saveError: null });
  },

  savePermissions: async (idToken: string, userId: string, selection: FolderSelection) => {
    set({ savingPermissions: true, saveError: null, saveSuccess: false });
    try {
      const payload = selectionToPolicy(selection);

      const response = await fetch(`${API_ENDPOINT}/access-policy/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Save failed' }));
        throw new Error(error.message || 'Failed to save permissions');
      }

      set({ savingPermissions: false, saveSuccess: true });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to save permissions';
      set({ savingPermissions: false, saveError: message });
    }
  },

  clearSaveStatus: () => {
    set({ saveError: null, saveSuccess: false });
  },
}));
